import clsx from "clsx";
import type { ReactNode } from "react";

// Section headings in the Boba Bash design are speech bubbles: a rounded pill
// with a small tail pointing down-left. Replaces the old angled banner.
export default function SpeechHeading({
  title,
  subtitle,
  eyebrow,
  actions,
  className,
  // A page has one h1. The landing stacks several of these as section
  // headings, so it asks for h2 rather than repeating the level.
  level: Heading = "h1",
}: {
  title: string;
  subtitle?: ReactNode;
  eyebrow?: string;
  actions?: ReactNode;
  className?: string;
  level?: "h1" | "h2";
}) {
  return (
    <div className={clsx("flex flex-col gap-4", className)}>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="relative inline-block">
          {/* Tail: a rotated square straddling the bubble's bottom edge, sitting
              behind it. The bubble's fill hides the top half and the seam, so
              only a cleanly bordered point shows. Clipping a bordered triangle
              instead leaves sliced border fragments. */}
          <div
            aria-hidden
            className="border-bubble-ink bg-bubble absolute bottom-0 left-9 z-0 h-4 w-4 translate-y-1/2 rotate-45 border-r-2 border-b-2"
          />

          <div className="border-bubble-ink bg-bubble relative z-10 inline-flex items-center gap-3 rounded-full border-2 px-6 py-2.5 sm:px-8 sm:py-3">
            {eyebrow && (
              <span className="font-display text-caption text-bubble-ink/70 font-bold tracking-wide uppercase">
                {eyebrow}
              </span>
            )}
            <Heading className="font-display text-heading text-bubble-ink sm:text-title font-bold">
              {title}
            </Heading>
          </div>
        </div>

        {actions}
      </div>

      {subtitle && (
        <div className="text-subheading text-ink-muted max-w-3xl leading-snug">
          {subtitle}
        </div>
      )}
    </div>
  );
}
